import { useCallback, useMemo, useRef, useState } from "react"
import { AppChatComposer } from "@/components/ui/app-chat/AppChatComposer"
import { AppChatConversation } from "@/components/ui/app-chat/AppChatConversation"
import { ChatPanelProvider, useChatPanel } from "@/contexts/ChatPanelContext"
import { MockChatController, type MockTransportMode } from "src/api/chat/MockChatController"
import { useMockChatSocket } from "src/hooks/chat/useMockChatSocket"
import type { UseChatSocketOptions } from "src/hooks/chat/useChatSocket"
import type { SendChatMessageRequest, SendChatMessageResponse } from "src/api/chat/Chat"

function ChatProgressTestInner() {
  const {
    messages,
    currentDraft,
    setCurrentDraft,
    currentScope,
    setCurrentScope,
    contextItems,
    setContextItems,
    removeContextItem,
    dynamicPageItems,
    sendMessage,
    isCurrentSessionLoading,
    settings,
    progressFrames,
  } = useChatPanel()

  return (
    <section className="flex min-w-0 flex-1 flex-col">
      <AppChatConversation
        messages={messages}
        settings={settings}
        isLoading={isCurrentSessionLoading}
        progressFrames={progressFrames}
        variant="page"
        emptyState={(
          <div className="space-y-2 text-center text-sm text-muted-foreground">
            <p className="font-medium">Chat Progress Test</p>
            <p>Send a message to see progress frames in action.</p>
          </div>
        )}
      />
      <AppChatComposer
        draft={currentDraft}
        onDraftChange={setCurrentDraft}
        onSubmit={() => sendMessage()}
        isLoading={isCurrentSessionLoading}
        scope={currentScope}
        onScopeChange={setCurrentScope}
        contextItems={contextItems}
        onContextItemsChange={setContextItems}
        onRemoveContextItem={removeContextItem}
        extraContextItems={dynamicPageItems}
        placeholder="Send anything to trigger mock progress frames..."
        variant="page"
      />
    </section>
  )
}

function useMockSocketFactory(controller: MockChatController, transportMode: MockTransportMode) {
  return function useSocket(options: UseChatSocketOptions) {
    useMockChatSocket({ ...options, controller, transportMode })
  }
}

export function ChatProgressTestPage() {
  const [transportMode, setTransportMode] = useState<MockTransportMode>("sse")
  const controllerRef = useRef<MockChatController>(new MockChatController())
  const controller = controllerRef.current

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const socketHook = useMemo(() => useMockSocketFactory(controller, transportMode), [controller, transportMode])

  const sendApi = useCallback(async (req: SendChatMessageRequest): Promise<SendChatMessageResponse> => {
    controller.simulateResponse(req.chatSessionId)
    return {
      providerMessageId: crypto.randomUUID(),
      workflowId: `mock-workflow-${Date.now()}`,
      acceptedAt: new Date().toISOString(),
    }
  }, [controller])

  return (
    <div className="flex h-full min-h-0 flex-col">
      <div className="flex items-center gap-4 border-b border-border px-6 py-3">
        <h1 className="text-lg font-semibold">Chat Progress Test</h1>
        <div className="flex items-center gap-2 rounded-lg border border-border px-3 py-1.5">
          <span className="text-xs text-muted-foreground">Transport:</span>
          <button
            type="button"
            onClick={() => setTransportMode("sse")}
            className={`rounded px-2 py-0.5 text-xs font-medium transition-colors ${
              transportMode === "sse"
                ? "bg-primary text-primary-foreground"
                : "text-muted-foreground hover:text-foreground"
            }`}
          >
            SSE
          </button>
          <button
            type="button"
            onClick={() => setTransportMode("polling")}
            className={`rounded px-2 py-0.5 text-xs font-medium transition-colors ${
              transportMode === "polling"
                ? "bg-primary text-primary-foreground"
                : "text-muted-foreground hover:text-foreground"
            }`}
          >
            REST Polling
          </button>
        </div>
        <span className="text-xs text-muted-foreground">
          {transportMode === "sse" ? "Frames arrive instantly via callback" : "Frames arrive every 2s via poll"}
        </span>
      </div>

      <div className="flex min-h-0 flex-1">
        <ChatPanelProvider socketHook={socketHook} sendApi={sendApi}>
          <ChatProgressTestInner />
        </ChatPanelProvider>
      </div>
    </div>
  )
}
